import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { FileText, CheckCircle, XCircle, Clock, DollarSign, TrendingUp, AlertCircle, Plane, Wallet, AlertTriangle, Globe, Home } from 'lucide-react';
import { formatINR } from '../utils/currency';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_COLORS = ['#4F46E5', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const fmtCur = (amount, currency) => {
  const val = Number(amount || 0);
  if (currency === 'USD') return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return formatINR(val);
};

const CUR_SYMBOLS = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

const Dashboard = () => {
  const { user } = useAuth();
  const { activeModule } = useModule();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const role = user?.role;
  const effectiveModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : activeModule;

  useEffect(() => {
    fetchDashboardData();
  }, [user, effectiveModule]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (['admin', 'management', 'hr', 'bdm'].includes(role)) {
        params.expense_type = effectiveModule;
      }
      const expParams = { ...params, limit: 10 };
      if (role === 'bdm') expParams.employee_id = user.id;

      const [statsRes, expensesRes] = await Promise.all([
        axios.get(`${API}/stats`, { params }),
        axios.get(`${API}/expenses`, { params: expParams })
      ]);
      setStats(statsRes.data);
      setRecentExpenses(expensesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Derive currency-separated data
  const byCurrency = stats?.by_currency || {};
  const currencies = useMemo(() => Object.keys(byCurrency).sort(), [byCurrency]);

  // For domestic: always exactly ["INR"]
  // For international: could be ["USD"], ["EUR"], or ["EUR", "USD"]
  const isDomesticView = effectiveModule === 'DOMESTIC';
  const primaryCurrency = isDomesticView ? 'INR' : (currencies.find(c => c !== 'INR') || 'USD');

  const getAgingLabel = (createdAt) => {
    if (!createdAt) return null;
    const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);
    if (days > 7) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">{'>'} 7d</span>;
    if (days > 3) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">{'>'} 3d</span>;
    return null;
  };

  const getStatusBadge = (expense) => {
    const s = expense.approval_status;
    const t = expense.settlement_status;
    const step = expense.current_approval_step;
    if (s === 'pending' && step && step !== 'done') {
      const stepLabels = { dgm: 'Awaiting DGM', management: 'Awaiting Mgmt', hr: 'Awaiting HR', accounts: 'Awaiting Accounts' };
      return <span className="status-badge status-pending">{stepLabels[step] || 'Pending'}</span>;
    }
    if (s === 'pending') return <span className="status-badge status-pending">Pending</span>;
    if (s === 'rejected') return <span className="status-badge status-rejected">Rejected</span>;
    if (t === 'reimbursed') return <span className="status-badge status-paid">Reimbursed</span>;
    if (t === 'company_paid') return <span className="status-badge status-approved">Company Paid</span>;
    return <span className="status-badge status-approved">Approved</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;

  const pendingCount = stats?.by_status?.pending || 0;
  const approvedCount = stats?.by_status?.approved || 0;
  const rejectedCount = stats?.by_status?.rejected || 0;
  const showCharts = ['admin', 'management', 'hr', 'dgm'].includes(role);
  const showPendingActions = ['admin', 'management', 'hr', 'dgm', 'accounts'].includes(role);
  const showReconciliation = ['admin', 'management', 'hr', 'accounts', 'dgm'].includes(role);
  const showLedger = effectiveModule === 'INTERNATIONAL' && ['admin', 'accounts'].includes(role);
  const pa = stats?.pending_actions;
  const recon = stats?.reconciliation_summary || [];
  const ledgerSummary = stats?.ledger_summary || [];

  // International may have multiple currencies — never mix them
  const intlCurrencies = currencies.filter(c => c !== 'INR');
  const hasMultipleIntlCurrencies = !isDomesticView && intlCurrencies.length > 1;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
            {isDomesticView
              ? <><Home className="w-3.5 h-3.5" /> Domestic Expense Overview (INR)</>
              : <><Globe className="w-3.5 h-3.5" /> International Expense Overview ({intlCurrencies.join(' / ') || 'USD/EUR'})</>
            }
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${
          isDomesticView ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        }`} data-testid="active-module-badge">
          {isDomesticView ? 'DOMESTIC' : 'INTERNATIONAL'}
        </div>
      </div>

      {/* Pending Actions */}
      {showPendingActions && pa && (pa.expenses_pending_approval > 0 || pa.trips_pending_approval > 0 || pa.pending_reimbursements_count > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pending-actions">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:shadow-sm transition-all" onClick={() => navigate('/expenses/pending-approval')} data-testid="pa-expenses-pending">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <span className="text-[10px] text-amber-600 font-medium uppercase">Expenses Pending</span>
            </div>
            <div className="text-lg font-bold text-amber-800">{pa.expenses_pending_approval}</div>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl cursor-pointer hover:shadow-sm transition-all" onClick={() => navigate('/trips?status=submitted')} data-testid="pa-trips-pending">
            <div className="flex items-center gap-2 mb-1">
              <Plane className="w-4 h-4 text-blue-500" />
              <span className="text-[10px] text-blue-600 font-medium uppercase">Trips to Approve</span>
            </div>
            <div className="text-lg font-bold text-blue-800">{pa.trips_pending_approval}</div>
          </div>
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl cursor-pointer hover:shadow-sm transition-all" onClick={() => navigate('/trips')} data-testid="pa-trips-unclosed">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-purple-500" />
              <span className="text-[10px] text-purple-600 font-medium uppercase">Trips Not Closed</span>
            </div>
            <div className="text-lg font-bold text-purple-800">{pa.trips_not_closed}</div>
          </div>
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl cursor-pointer hover:shadow-sm transition-all" onClick={() => navigate('/expenses/pending-payment')} data-testid="pa-reimbursements">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-rose-500" />
              <span className="text-[10px] text-rose-600 font-medium uppercase">Pending Reimbursements</span>
            </div>
            <div className="text-lg font-bold text-rose-800">{pa.pending_reimbursements_count}</div>
          </div>
        </div>
      )}

      {/* Currency-Separated Totals */}
      {isDomesticView ? (
        /* DOMESTIC: single-currency metric cards */
        <DomesticMetricCards stats={stats} byCurrency={byCurrency} navigate={navigate} />
      ) : (
        /* INTERNATIONAL: per-currency metric cards */
        <InternationalMetricCards byCurrency={byCurrency} intlCurrencies={intlCurrencies} stats={stats} navigate={navigate} />
      )}

      {/* International Ledger Summary */}
      {showLedger && ledgerSummary.length > 0 && (
        <div className="card mb-6" data-testid="ledger-summary">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Company Ledger Balances</h3>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Employee</th><th>Account</th><th>Currency</th><th>Balance</th></tr></thead>
              <tbody>
                {ledgerSummary.map(l => (
                  <tr key={l.id} data-testid={`ledger-row-${l.id}`}>
                    <td className="font-medium">{l.user_name}</td>
                    <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${l.account_type === 'CARD' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>{l.account_type}</span></td>
                    <td>{l.currency}</td>
                    <td className="font-semibold">{fmtCur(l.balance, l.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reconciliation Summary */}
      {showReconciliation && recon.length > 0 && (
        <div className="card mb-6" data-testid="reconciliation-summary">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Advance vs Expense Reconciliation</h3>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Trip</th><th>Type</th><th>Employee</th><th>Currency</th><th>Advance</th><th>Spent</th><th>Balance</th><th>Status</th></tr></thead>
              <tbody>
                {recon.map(r => {
                  const cur = r.currency || 'INR';
                  return (
                    <tr key={r.trip_id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/trips/${r.trip_id}`)} data-testid={`recon-${r.trip_id}`}>
                      <td className="font-medium">{r.trip_name}</td>
                      <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.trip_type === 'Domestic' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>{r.trip_type}</span></td>
                      <td>{r.employee_name}</td>
                      <td className="text-xs text-gray-500">{cur}</td>
                      <td>{fmtCur(r.advance, cur)}</td>
                      <td>{fmtCur(r.spent, cur)}</td>
                      <td>
                        {r.balance >= 0
                          ? <span className="text-emerald-600 font-semibold">{fmtCur(r.balance, cur)}</span>
                          : <span className="text-red-600 font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Overused {fmtCur(Math.abs(r.balance), cur)}</span>
                        }
                      </td>
                      <td>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                          r.status === 'settled' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          r.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          r.status === 'submitted' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>{r.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts — currency-aware */}
      {showCharts && <CurrencyAwareCharts byCurrency={byCurrency} isDomesticView={isDomesticView} intlCurrencies={intlCurrencies} />}

      {/* Recent Expenses */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Recent Expenses</h2>
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Date</th><th>Category</th><th>Vendor</th><th>Currency</th><th>Amount</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {recentExpenses.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-gray-500 py-8">No {effectiveModule.toLowerCase()} expenses found</td></tr>
              ) : (
                recentExpenses.map(expense => (
                  <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                    <td>{new Date(expense.expense_date).toLocaleDateString()}</td>
                    <td>{expense.category}</td>
                    <td>
                      {expense.vendor}
                      {expense.duplicate_warning && <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded" data-testid="dup-badge">DUP</span>}
                    </td>
                    <td className="text-xs text-gray-500">{expense.currency || 'INR'}</td>
                    <td className="font-semibold">{fmtCur(expense.amount, expense.currency || 'INR')}</td>
                    <td>{getStatusBadge(expense)}</td>
                    <td>{expense.approval_status === 'pending' && getAgingLabel(expense.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


/* ==================== DOMESTIC METRIC CARDS ==================== */
const DomesticMetricCards = ({ stats, byCurrency, navigate }) => {
  const inrData = byCurrency['INR'] || {};
  const approvedTotal = inrData.approved_total || 0;
  const pendingCount = stats?.by_status?.pending || 0;
  const approvedCount = stats?.by_status?.approved || 0;
  const rejectedCount = stats?.by_status?.rejected || 0;
  const pendingReimb = inrData.pending_reimbursements || 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6" data-testid="domestic-metric-cards">
      <StatCard icon={DollarSign} color="indigo" label="Total Approved" value={fmtCur(approvedTotal, 'INR')} onClick={() => navigate('/expenses/all?approval_status=approved')} testId="stat-total-approved" />
      <StatCard icon={FileText} color="blue" label="Total Requests" value={stats?.total_count || 0} onClick={() => navigate('/expenses/all')} testId="stat-total-count" />
      <StatCard icon={Clock} color="amber" label="Pending" value={pendingCount} onClick={() => navigate('/expenses/pending-approval')} testId="stat-pending" />
      <StatCard icon={CheckCircle} color="emerald" label="Approved" value={approvedCount} onClick={() => navigate('/expenses/all?approval_status=approved')} testId="stat-approved" />
      <StatCard icon={XCircle} color="red" label="Rejected" value={rejectedCount} onClick={() => navigate('/expenses/all?approval_status=rejected')} testId="stat-rejected" />
      <StatCard icon={Wallet} color="rose" label="Pending Reimb" value={fmtCur(pendingReimb, 'INR')} onClick={() => navigate('/expenses/pending-payment')} testId="stat-reimb" />
    </div>
  );
};


/* ==================== INTERNATIONAL METRIC CARDS ==================== */
const InternationalMetricCards = ({ byCurrency, intlCurrencies, stats, navigate }) => {
  if (intlCurrencies.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6" data-testid="intl-metric-cards">
        <StatCard icon={DollarSign} color="emerald" label="Total Approved (USD)" value="$0.00" onClick={() => navigate('/expenses/all')} testId="stat-total-usd" />
        <StatCard icon={FileText} color="blue" label="Total Requests" value={stats?.total_count || 0} onClick={() => navigate('/expenses/all')} testId="stat-total-count" />
        <StatCard icon={Clock} color="amber" label="Pending" value={stats?.by_status?.pending || 0} onClick={() => navigate('/expenses/pending-approval')} testId="stat-pending" />
        <StatCard icon={CheckCircle} color="emerald" label="Approved" value={stats?.by_status?.approved || 0} onClick={() => navigate('/expenses/all?approval_status=approved')} testId="stat-approved" />
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="intl-metric-cards">
      {intlCurrencies.map(cur => {
        const data = byCurrency[cur] || {};
        return (
          <div key={cur} className="mb-4">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: cur === 'USD' ? '#10B981' : '#6366F1' }} />
              {cur} Expenses
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatCard icon={DollarSign} color={cur === 'USD' ? 'emerald' : 'indigo'} label={`Approved (${cur})`} value={fmtCur(data.approved_total || 0, cur)} onClick={() => navigate('/expenses/all?approval_status=approved')} testId={`stat-approved-total-${cur.toLowerCase()}`} />
              <StatCard icon={FileText} color="blue" label={`Count (${cur})`} value={data.total_count || 0} onClick={() => navigate('/expenses/all')} testId={`stat-count-${cur.toLowerCase()}`} />
              <StatCard icon={Clock} color="amber" label={`Pending (${cur})`} value={data.pending_count || 0} onClick={() => navigate('/expenses/pending-approval')} testId={`stat-pending-${cur.toLowerCase()}`} />
              <StatCard icon={CheckCircle} color="emerald" label={`Approved (${cur})`} value={data.approved_count || 0} onClick={() => navigate('/expenses/all?approval_status=approved')} testId={`stat-approved-count-${cur.toLowerCase()}`} />
              <StatCard icon={XCircle} color="red" label={`Rejected (${cur})`} value={data.rejected_count || 0} onClick={() => navigate('/expenses/all?approval_status=rejected')} testId={`stat-rejected-${cur.toLowerCase()}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
};


/* ==================== STAT CARD ==================== */
const STAT_COLORS = {
  indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-l-indigo-500' },
  blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-l-blue-500' },
  amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-l-amber-500' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-l-emerald-500' },
  red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-l-red-500' },
  rose: { bg: 'bg-rose-50', icon: 'text-rose-600', border: 'border-l-rose-500' },
};

const StatCard = ({ icon: Icon, color, label, value, onClick, testId }) => {
  const c = STAT_COLORS[color] || STAT_COLORS.indigo;
  return (
    <div className={`stat-card cursor-pointer hover:shadow-md transition-all border-l-4 ${c.border}`} onClick={onClick} data-testid={testId}>
      <div className={`w-8 h-8 ${c.bg} rounded-lg flex items-center justify-center mb-2`}>
        <Icon className={`w-4 h-4 ${c.icon}`} />
      </div>
      <div className="text-lg md:text-xl font-bold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
};


/* ==================== CURRENCY-AWARE CHARTS ==================== */
const CurrencyAwareCharts = ({ byCurrency, isDomesticView, intlCurrencies }) => {
  // For domestic: use INR data only
  // For international: render charts per currency, never mix
  const chartCurrencies = isDomesticView ? ['INR'] : intlCurrencies;

  if (chartCurrencies.length === 0) {
    return (
      <div className="card mb-6">
        <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No international expense data</div>
      </div>
    );
  }

  return (
    <>
      {chartCurrencies.map(cur => {
        const data = byCurrency[cur] || {};
        const categoryData = data.by_category
          ? Object.entries(data.by_category).map(([name, value]) => ({ name, value }))
          : [];
        const monthlyTrend = data.monthly_trend || [];
        const topSpenders = data.top_spenders || [];
        const gradientId = `colorAmount_${cur}`;
        const accentColor = cur === 'USD' ? '#10B981' : cur === 'EUR' ? '#6366F1' : '#4F46E5';

        const CurTooltip = ({ active, payload, label }) => {
          if (active && payload && payload.length) {
            return (
              <div className="bg-white px-3 py-2 shadow-lg rounded-lg border border-gray-200 text-sm">
                <p className="text-gray-600 mb-0.5">{label || payload[0]?.name}</p>
                <p className="font-semibold text-gray-900">{fmtCur(payload[0]?.value, cur)}</p>
              </div>
            );
          }
          return null;
        };

        return (
          <div key={cur} className="mb-6">
            {!isDomesticView && (
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: accentColor }} />
                {cur} Analytics
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-4">
              <div className="card" data-testid={`chart-monthly-trend-${cur.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Monthly Trend {!isDomesticView && `(${cur})`}</h3>
                </div>
                {monthlyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={monthlyTrend}>
                      <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={accentColor} stopOpacity={0.15} />
                          <stop offset="95%" stopColor={accentColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={(v) => `${CUR_SYMBOLS[cur] || ''}${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={<CurTooltip />} />
                      <Area type="monotone" dataKey="amount" stroke={accentColor} strokeWidth={2} fill={`url(#${gradientId})`} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <div className="h-56 flex items-center justify-center text-gray-400 text-sm">No {cur} data</div>}
              </div>
              <div className="card" data-testid={`chart-by-category-${cur.toLowerCase()}`}>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">By Category {!isDomesticView && `(${cur})`}</h3>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={categoryData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                        {categoryData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CurTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="h-56 flex items-center justify-center text-gray-400 text-sm">No data</div>}
              </div>
            </div>

            {topSpenders.length > 0 && (
              <div className="card mb-4" data-testid={`chart-top-spenders-${cur.toLowerCase()}`}>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Top Spenders {!isDomesticView && `(${cur})`}</h3>
                <ResponsiveContainer width="100%" height={Math.max(180, topSpenders.length * 40)}>
                  <BarChart data={topSpenders} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={(v) => `${CUR_SYMBOLS[cur] || ''}${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} width={110} />
                    <Tooltip content={<CurTooltip />} />
                    <Bar dataKey="amount" fill={accentColor} radius={[0, 4, 4, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};


export default Dashboard;
